
import { useState, useEffect } from 'react';
import { toast } from "sonner";

interface UsePersistJourneyDataProps<T> {
  storageKey: string;
  defaultValue: T;
}

export const usePersistJourneyData = <T,>({ 
  storageKey, 
  defaultValue 
}: UsePersistJourneyDataProps<T>): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [data, setData] = useState<T>(defaultValue);
  
  // Load data from localStorage on initial render
  useEffect(() => {
    const savedData = localStorage.getItem(storageKey);
    
    if (savedData) {
      try {
        setData(JSON.parse(savedData) as T);
      } catch (error) {
        console.error(`Failed to parse saved data for ${storageKey}`, error);
        setData(defaultValue);
      }
    }
  }, [storageKey, defaultValue]);
  
  // Save data to localStorage whenever it changes
  useEffect(() => {
    if (data) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch (error) {
        console.error(`Failed to save data for ${storageKey}`, error);
        toast.error(`Failed to save data. Storage limit might be exceeded.`);
      }
    }
  }, [data, storageKey]);
  
  return [data, setData];
};
